<template>
    <div class="d-flex flex-column">

        <p class="mb-1">{{ $t('chooseWhichStoresYouWantToDisplayInYourWidgets') }}</p>

        <div class="mb-4">
            <ButtonIcon
                icon="file-text"
                :icon-size="24"
                href="https://getbuybox.com/konfiguracje/"
                target="_blank"
                variant="link"
                class="p-0"
            >
                {{ $t('readMoreAboutShoppingListManagement') }}
            </ButtonIcon>
        </div>

        <b-form-group id="filter-search-campaign" :label="$t('searchCampaign')" class="mb-3">
            <form class="d-flex gap-2 width-max-800" @keyup.enter="filterCampaigns()">
                <b-form-input id="filter-name" v-model="filters.name" :placeholder="$t('enterCampaignName')" />
                <Multiselect
                    id="filter-categories"
                    :placeholder="$t('selectCategory')"
                    :fetch-options-function="fetchCategories"
                    track-by="id"
                    label="name"
                    multiple
                    show-checkboxes
                    show-selected
                    :close-on-select="false"
                    @inputTrackBy="filters.categories = $event"
                />
                <ButtonIcon class="wide" :disabled="loading" @click="filterCampaigns()">{{ $t('filter') }}</ButtonIcon>
            </form>
        </b-form-group>

        <div class="row gx-3 gy-4">
            <div class="col-12 col-md-6 d-flex flex-column">
                <h5 class="mb-1"><span id="WTB-Sklepy-header-1" class="text-link-header">{{ $t('availableCampaigns') }}</span></h5>
                <template v-if="loading">
                    <div class="d-flex justify-content-center my-2">
                        <b-spinner />
                    </div>
                </template>
                <template v-else>
                    <div class="d-flex justify-content-between align-items-center border-bottom py-3">
                        <span id="WTB-Sklepy-label-1" class="font-weight-bold text-link-label-1">{{ $t('campaign') }}</span>
                        <b-button variant="link" class="m-0 p-0" :disabled="!removedCampaigns.length || requestCampaigns.getLoading('all')" @click="addAllCampaigns()">{{ $t('addAll') }}</b-button>
                    </div>
                    <TransitionGroup
                        v-show="removedCampaigns.length"
                        class="list-unstyled p-0 m-0"
                        tag="ul"
                        name="height-opacity-transition"
                        @before-enter="beforeEnter"
                        @enter="enter"
                        @leave="leaveWithOpacity"
                    >
                        <template v-for="(item, index) in campaignsList">
                            <li v-if="!item.isAdded" :key="`available-campaigns-${item.id}`" class="item">
                                <div class="py-3 px-0 d-flex align-items-center border-bottom gap-3">
                                    <img :src="item.icon" alt="" width="24" height="24">
                                    <span class="text-truncate">{{ item.name }}</span>
                                    <ButtonIcon
                                        variant="success"
                                        class="wide ml-auto px-2 py-1"
                                        :loading="requestCampaigns.getLoading(item.id)"
                                        :disabled="requestCampaigns.getLoading('all')"
                                        hide-label-while-loading
                                        @click="addCampaign(item, index)"
                                    >
                                        {{ $t('add') }}
                                    </ButtonIcon>
                                </div>
                            </li>
                        </template>
                    </TransitionGroup>
                    <div v-show="!removedCampaigns.length" class="text-center p-3 border-bottom">{{ $t('listIsEmpty') }}</div>
                </template>
            </div>

            <div class="col-12 col-md-6 d-flex flex-column">
                <h5 class="mb-2"><span id="WTB-Sklepy-header-2" class="text-link-header">{{ $t('activeCampaigns') }}</span></h5>
                <template v-if="loading">
                    <div class="d-flex justify-content-center my-2">
                        <b-spinner />
                    </div>
                </template>
                <template v-else>
                    <div class="d-flex gap-3 align-items-center border-bottom py-3">
                        <div class="d-flex gap-3 flex-grow-1">
                            <span id="WTB-Sklepy-label-2" class="font-weight-bold flex-grow-2 flex-basis-0 text-link-label-1">{{ $t('campaign') }}</span>
                            <span id="WTB-Sklepy-label-3" class="font-weight-bold mx-4 flex-grow-1 flex-basis-0 text-link-label-1 d-none d-md-block">{{ $t('weight') }}</span>
                        </div>
                        <div class="width-140 d-flex align-items-center">
                            <b-button variant="link" class="m-0 p-0 ml-auto" :disabled="!addedCampaigns.length || requestCampaigns.getLoading('all')" @click="removeAllCampaigns()">{{ $t('removeAll') }}</b-button>
                        </div>
                    </div>
                    <TransitionGroup
                        v-show="addedCampaigns.length"
                        class="list-unstyled p-0 m-0"
                        tag="ul"
                        name="height-opacity-transition"
                        @before-enter="beforeEnter"
                        @enter="enterWithOpacity"
                        @leave="leaveWithOpacity"
                    >
                        <template v-for="(item, index) in campaignsList">
                            <li v-if="item.isAdded" :key="`active-campaigns-${item.id}`" class="item">
                                <div class="py-3 px-0 d-flex align-items-center border-bottom gap-3">
                                    <div class="d-flex flex-wrap gap-3 flex-grow-1">
                                        <div class="d-flex gap-3 flex-grow-2 flex-basis-0">
                                            <img :src="item.icon" alt="" width="24" height="24">
                                            <span class="text-truncate">{{ item.name }}</span>
                                        </div>
                                        <div class="flex-grow-1 flex-basis-0 d-flex items-center justify-content-center">
                                            <span class="font-weight-bold mr-2 my-0 text-link-label-1 d-md-none">{{ $t('weight') }}</span>
                                            <b-input
                                                :id="`form-priority-${item.id}`"
                                                type="number"
                                                class="width-100 h-auto p-1 pl-2"
                                                :value="item.priority"
                                                :disabled="requestCampaigns.getLoading(`priority-${item.id}`) || requestCampaigns.getLoading('all')"
                                                @change="updatePriority($event, item, index)"
                                            />
                                        </div>
                                    </div>
                                    <ButtonIcon
                                        variant="danger"
                                        class="wide ml-auto px-2 py-1"
                                        :loading="requestCampaigns.getLoading(item.id)"
                                        :disabled="requestCampaigns.getLoading('all')"
                                        hide-label-while-loading
                                        @click="removeCampaign(item, index)"
                                    >
                                        {{ $t('remove') }}
                                    </ButtonIcon>
                                </div>
                            </li>
                        </template>
                    </TransitionGroup>
                    <div v-show="!addedCampaigns.length" class="text-center p-3 border-bottom">{{ $t('listIsEmpty') }}</div>
                </template>
            </div>

        </div>

    </div>
</template>

<script>
    /* eslint-disable no-param-reassign */
    import cloneDeep from 'lodash/cloneDeep';
    import categoryService from '@/services/categoryService';
    import Multiselect from '@/components/common/Multiselect.vue';

    export default {
        name: 'Campaigns',
        components: {
            Multiselect,
        },
        data() {
            return {
                id: Number.parseInt(this.$route.params.id),
                requestActiveCampaigns: this.$req('buyboxes/activeCampaigns'),
                requestAvailableCampaigns: this.$req('buyboxes/availableCampaigns'),
                requestCampaigns: this.$req('buyboxes/campaigns'),
                filters: {
                    name: '',
                    categories: [],
                },
                campaignsList: [],
                animationTime: 300,
            };
        },
        computed: {
            loading() {
                return this.requestActiveCampaigns.getLoading() || this.requestAvailableCampaigns.getLoading();
            },
            addedCampaigns() {
                return this.campaignsList.filter(item => item.isAdded);
            },
            removedCampaigns() {
                return this.campaignsList.filter(item => !item.isAdded);
            },
        },
        methods: {
            fetchCategories() {
                return categoryService.getCategories();
            },
            filterCampaigns() {
                this.campaignsList = [];
                const p1 = this.fetchActiveCampaigns();
                const p2 = this.fetchAvailableCampaigns(this.filters);
                Promise.all([p1, p2]).then(this.initAvailableCampaigns);
            },
            fetchCampaigns() {
                const p1 = this.fetchActiveCampaigns();
                const p2 = this.fetchAvailableCampaigns();
                return Promise.all([p1, p2]).then(this.initAvailableCampaigns);
            },
            fetchActiveCampaigns() {
                return this.requestActiveCampaigns.get({ urlParams: { id: this.id } });
            },
            fetchAvailableCampaigns(filters) {
                return this.requestAvailableCampaigns.get({ urlParams: { id: this.id }, requestParams: filters });
            },
            updatePriority(priority, item, index) {
                const id = `priority-${item.id}`;
                const isLoading = this.requestCampaigns.getLoading(id);
                if (!isLoading) {
                    this.requestCampaigns
                        .id(id)
                        .patch({ urlParams: { id: this.id, campaignId: item.id }, requestParams: { priority } })
                        .then(response => {
                            this.$set(item, 'priority', response.priority);
                            this.$set(this.campaignsList, index, item);
                        })
                        .catch(error => {
                            this.$toastr.error(error.message, this.$t('errorOccurred'));
                        });
                }
            },
            addCampaign(item, index) {
                this.requestCampaigns
                    .id(item.id)
                    .post({ urlParams: { id: this.id, campaignId: item.id } })
                    .then(() => {
                        this.$set(item, 'isAdded', true);
                        this.$set(this.campaignsList, index, item);
                    })
                    .catch(error => {
                        this.$toastr.error(error.message, this.$t('errorOccurred'));
                    });
            },
            removeCampaign(item, index) {
                this.requestCampaigns
                    .id(item.id)
                    .delete({ urlParams: { id: this.id, campaignId: item.id } })
                    .then(() => {
                        this.$set(item, 'isAdded', false);
                        this.$set(this.campaignsList, index, item);
                    })
                    .catch(error => {
                        this.$toastr.error(error.message, this.$t('errorOccurred'));
                    });
            },
            initAvailableCampaigns() {
                this.campaignsList = cloneDeep(this.requestAvailableCampaigns.data);
                this.campaignsList.forEach(campaign => {
                    const activeCampaign = this.requestActiveCampaigns.data.find(item => item.id === campaign.id);
                    campaign.isAdded = !!activeCampaign;
                    campaign.priority = activeCampaign?.priority;
                });
            },
            addAllCampaigns() {
                const campaignIds = [];
                this.campaignsList.forEach(item => {
                    if (item.isAdded === false) {
                        campaignIds.push(item.id);
                    }
                });
                this.requestCampaigns
                    .id('all')
                    .post({ urlParams: { id: this.id }, requestParams: { campaigns: campaignIds } })
                    .then(() => {
                        this.campaignsList.forEach((item, index) => {
                            this.$set(item, 'isAdded', true);
                            this.$set(this.campaignsList, index, item);
                        });
                    })
                    .catch(error => {
                        this.$toastr.error(error.message, this.$t('errorOccurred'));
                    });
            },
            removeAllCampaigns() {
                const campaignIds = [];
                this.campaignsList.forEach(item => {
                    if (item.isAdded === true) {
                        campaignIds.push(item.id);
                    }
                });
                this.requestCampaigns
                    .id('all')
                    .delete({ urlParams: { id: this.id }, requestParams: { campaigns: campaignIds } })
                    .then(() => {
                        this.campaignsList.forEach((item, index) => {
                            this.$set(item, 'isAdded', false);
                            this.$set(this.campaignsList, index, item);
                        });
                    })
                    .catch(error => {
                        this.$toastr.error(error.message, this.$t('errorOccurred'));
                    });
            },
            beforeEnter(el) {
                el.style.height = '0';
                el.style.opacity = '0';
            },
            enter(el) {
                el.style.height = `${el.scrollHeight}px`;
                el.style.opacity = '1';
                el.style.transition = 'height 0.3s ease, opacity 0.3s ease 0.3s';
                setTimeout(() => {
                    el.style.height = 'auto';
                }, this.animationTime);
            },
            enterWithOpacity(el) {
                el.style.height = '0';
                el.style.opacity = '0';
                el.style.transition = 'height 0.3s ease, opacity 0.3s ease';
                requestAnimationFrame(() => {
                    el.style.height = `${el.scrollHeight}px`;
                    el.style.opacity = '1';
                });
                setTimeout(() => {
                    el.style.height = 'auto';
                }, this.animationTime);
            },
            leaveWithOpacity(el) {
                el.style.height = `${el.scrollHeight}px`;
                el.style.opacity = '1';
                el.style.transition = 'opacity 0.3s ease, height 0.3s ease 0.3s';
                requestAnimationFrame(() => {
                    el.style.opacity = '0';
                    el.style.height = '0';
                });
            },
        },
        created() {
            this.fetchCampaigns();
        },
    };
</script>

<style lang="scss" scoped>
    /* Animacje wysokości i przezroczystości */
    .height-opacity-transition-enter-active,
    .height-opacity-transition-leave-active {
        transition: height 0.3s ease, opacity 0.3s ease;
    }
    .height-opacity-transition-enter-from,
    .height-opacity-transition-leave-to {
        height: 0;
        opacity: 0;
    }
    .height-opacity-transition-leave-active {
        overflow: hidden;
    }
</style>
